import { useLocation } from '@solidjs/router';
import clsx from 'clsx';
import { gql } from 'graphql-request';
import { Match, Show, Switch, createEffect, createSignal, onMount, createMemo } from 'solid-js';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { breakpoints } from '../../style/theme';
import { ErrorCatcher } from '../../tools/error-catcher';
import { navigateToAnchor } from '../../tools/scroll-to-element';
import { NotFound } from '../404/404';
import { BlogPostIntroContainer } from '../blog-post/blog-post-intro';
import { NewsPostIntroContainer } from '../news-post/news-post-intro';
import { Schema } from '../schema/schema';
import { SeoSettings } from '../seo-settings/seo-settings';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import { MovingCircles } from '../moving-circles/moving-circles';
import { Heading } from '../../ui-components/heading/heading';

// Recursively checks the block tree for something that already renders an <h1>:
// either the hero block (always renders h1) or a heading block with tag="h1".
const hasH1Block = (blocks: any[] | undefined | null): boolean => {
    if (!blocks?.length) return false;
    return blocks.some((b) => {
        if (b?.blockName === 'apelsin/hero') return true;
        if (b?.blockName === 'apelsin/heading' && b?.tag === 'h1') return true;
        return hasH1Block(b?.blocks);
    });
};

export const GET_PAGE_QUERY = gql`
    query GetPage($key: String!) {
        page: resource(key: $key) {
            content
            key
        }
    }
`;

export const PageContainer = (props: any) => {
    const { createCachedResource, viewport, setStatusCode, pageTheme } = useAppContext();
    const isMobile = () => viewport.width <= breakpoints.mobile;
    const location = useLocation();
    const [currentLocation, setCurrentLocation] = createSignal(location.pathname);

    const getCurrentPathName = () => {
        return removeTrailingSlash(location.pathname.toLowerCase());
    };

    const [pageContent] = createCachedResource(GET_PAGE_QUERY, () => ({ key: getCurrentPathName() }), true, { trackPath: getCurrentPathName });
    if (pageContent()?.page?.content?.type === 'client' && !pageContent()?.page?.content?.blocks?.length) {
        setStatusCode(404);
        return <NotFound />;
    }
    // const [pageContent] = createCachedResource(GET_PAGE_QUERY, () => ({ key: removeTrailingSlash(location.pathname.toLowerCase()) }), true, { trackPath: removeTrailingSlash(location.pathname.toLowerCase()) });
    const [hasHero, setHasHero] = createSignal(pageContent()?.page?.content?.blocks[0].blockName === 'apelsin/hero');
    const [featuredImage, setFeaturedImage] = createSignal(pageContent()?.page?.content?.featured_image, { equals: (a, b) => a?.url === b?.url });

    /**
     * TODO: Not sure this works - if there's an anchor in the url the page only sometimes smoothly scrolls down
     */
    onMount(() => {
        if (location.hash) {
            navigateToAnchor({ id: location.hash.replace('#', ''), smallScreen: isMobile() });
        }
    });
    
    const found = () => {
        if (pageContent.loading) {
            return true;
        }
        if (pageContent()?.page?.content) {
            return true;
        }
        setStatusCode(404);
        return false;
    };

    createEffect(() => {       
        setHasHero(pageContent()?.page?.content?.blocks[0]?.blockName === 'apelsin/hero');

        if (currentLocation() !== location.pathname) {
            window.scrollTo({ top: 0 }); //, behavior: 'smooth' });
            
            setCurrentLocation(location.pathname);
            setFeaturedImage(pageContent()?.page?.content?.featured_image);
        }
    });

    const movingCirclesColor = createMemo(() => pageContent()?.page?.content?.moving_circles?.moving_circles_color);
    const movingCirclesPosition = createMemo(() => pageContent()?.page?.content?.moving_circles?.moving_circles_position);
    const isClientPage = createMemo(() => pageContent()?.page?.content?.type === 'client');

    return (
        <ErrorCatcher componentName="Page">
            <Show when={!found()}>
                <NotFound />
            </Show>
            <Show when={found()}>
                <div
                    class={clsx(
                        { 'pt-[10rem]': !hasHero() },
                        { 'desktop:pt-[16rem]': !hasHero() },
                        { 'tablet:pt-[12rem]': !hasHero() },
                        pageTheme().colors.classes,
                        'relative',
                    )}
                >
                    <Switch>
                        <Match when={pageContent()?.page?.content}>
                            <Show when={pageContent()?.page?.content?.moving_circles?.use_moving_circles} keyed>
                                <MovingCircles 
                                    movingCirclesColor={movingCirclesColor} 
                                    movingCirclesPosition={movingCirclesPosition} 
                                    hash={pageContent()?.page?.content?.id}
                                    isClientPage={isClientPage}
                                />
                            </Show>
                            <SeoSettings currentUrl={() => pageContent()?.page?.content?.permalink} seo={() => pageContent()?.page?.content?.seo} title={pageContent()?.page?.content?.post_title} />
                            <Show when={pageContent()?.page?.content?.type === 'blog'}>
                                <BlogPostIntroContainer
                                    author={pageContent()?.page?.content?.author}
                                    date={pageContent()?.page?.content?.date}
                                    categories={pageContent()?.page?.content?.categories}
                                    featuredImage={pageContent()?.page?.content?.featured_image}
                                    postTitle={pageContent()?.page?.content?.post_title}
                                    preamble={pageContent()?.page?.content?.preamble}
                                    readingTime={pageContent()?.page?.content?.reading_time}
                                    strippedContent={pageContent()?.page?.content?.stripped_content}
                                    postType={pageContent()?.page?.content?.post_type}
                                    backgroundColor={pageTheme()?.colors?.bgColor}
                                />
                            </Show>
                            <Show when={pageContent()?.page?.content?.type === 'news'}>
                                <NewsPostIntroContainer
                                    preamble={pageContent()?.page?.content?.preamble}
                                    date={pageContent()?.page?.content?.date}
                                    featuredImage={pageContent()?.page?.content?.featured_image}
                                    postTitle={pageContent()?.page?.content?.post_title}
                                    strippedContent={pageContent()?.page?.content?.stripped_content}
                                />
                            </Show>
                            <Show
                                when={
                                    pageContent()?.page?.content?.type !== 'blog' &&
                                    pageContent()?.page?.content?.type !== 'news' &&
                                    pageContent()?.page?.content?.post_title &&
                                    !hasH1Block(pageContent()?.page?.content?.blocks)
                                }
                            >
                                <div class="px-containerPadding">
                                    <Heading tag="h1" align="center" fontSize="xxl" classes="mx-auto">{pageContent()?.page?.content?.post_title}</Heading>
                                </div>
                            </Show>

                            <DynamicModuleGenerator
                                content={pageContent()?.page?.content.blocks}
                                categories={pageContent()?.page?.content.categoriesFromHook}
                                permalink={pageContent()?.page?.content.permalink}
                                pageTitle={pageContent()?.page?.content.post_title}
                                postType={props.postType}
                            />
                        </Match>
                    </Switch>
                </div>

                <Schema content={pageContent()?.page?.content} />
            </Show>
        </ErrorCatcher>
    );
};
